import type { MetricsRow } from 'app/services/metrics'
import type { ColumnDef } from '@tanstack/react-table'
import type { FormEvent } from 'react'
import { useRef } from 'react'
import { PageScroll } from 'app/components/layout/PageScroll'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { PageHeader } from 'app/components/ui/page-header'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'app/components/ui/dialog'
import type { FilterableColumn } from 'app/filter.utils'
import SimpleTable from 'app/components/table/SimpleTable'

export type WorkspacesShadcnComponentProps = {
  columns: ColumnDef<MetricsRow, any>[]
  filterableColumns: FilterableColumn[]
  filteredRows: MetricsRow[] | undefined
  totalItems: number
  canCreate: boolean
  open: boolean
  saving: boolean
  setOpen: (open: boolean) => void
  setSaving: (saving: boolean) => void
  onSearchValueChange: (val: string | undefined) => void
  onCreateWorkspace: (name: string) => Promise<void>
}

export function WorkspacesShadcnComponent({
  columns,
  filterableColumns,
  filteredRows,
  totalItems,
  canCreate,
  open,
  saving,
  setOpen,
  setSaving,
  onSearchValueChange,
  onCreateWorkspace,
}: WorkspacesShadcnComponentProps) {
  const formRef = useRef<HTMLFormElement>(null)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const name = (formData.get(`name`) as string)?.trim()
    if (!name) return
    setSaving(true)
    onCreateWorkspace(name)
      .then(() => {
        formRef.current?.reset()
      })
      .catch(() => {})
      .finally(() => {
        setSaving(false)
      })
  }
  return (
    <>
      <PageScroll
        variant="table"
        header={
          <PageHeader
            totalItems={totalItems}
            title="Workspaces"
            primaryButtonSlot={
              canCreate ? (
                <Button variant="primary" onClick={() => setOpen(true)}>
                  Add Workspace
                </Button>
              ) : undefined
            }
          />
        }
      >
        <SimpleTable
          data={filteredRows as MetricsRow[]}
          columns={columns}
          showColumnOrdering={true}
          filterableColumns={filterableColumns}
          onSearchValueChange={onSearchValueChange}
          enableExport
          edgePadded
        />
      </PageScroll>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Create a workspace</DialogTitle>
            <DialogDescription className="sr-only">Enter a name for the new workspace</DialogDescription>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="py-2">
              <Label htmlFor="workspace-name">Name</Label>
              <Input id="workspace-name" name="name" required className="mt-1" />
            </div>
            <DialogFooter className="mt-4">
              <Button variant="white" type="button" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" loading={saving} disabled={saving}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
